<script setup lang="ts">
  import { ref, watch } from "vue";
  import { FileUp, Loader2 } from "lucide-vue-next";
  import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { useJcdCampaignsStore } from "@stores/jcdCampaigns";

  const props = defineProps<{
    open: boolean;
  }>();

  const emit = defineEmits<{
    "update:open": [value: boolean];
  }>();

  const store = useJcdCampaignsStore();

  const file = ref<File | null>(null);
  const isSubmitting = ref(false);

  watch(
    () => props.open,
    (open) => {
      if (open) file.value = null;
    },
  );

  const handleFileChange = (event: Event) => {
    file.value = (event.target as HTMLInputElement).files?.[0] || null;
  };

  const handleSubmit = async () => {
    if (!file.value) return;
    isSubmitting.value = true;
    const started = await store.importCampaigns(file.value);
    isSubmitting.value = false;
    if (started) emit("update:open", false);
  };
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Importer des campagnes</DialogTitle>
        <DialogDescription>
          Fichier CSV au même format que l'export (séparateur point-virgule). Les campagnes en doublon
          (même marque, même semaine, même année) seront exclues de l'import.
        </DialogDescription>
      </DialogHeader>
      <Input
        type="file"
        accept=".csv,text/csv"
        @change="handleFileChange" />
      <DialogFooter>
        <Button
          :disabled="!file || isSubmitting"
          @click="handleSubmit">
          <Loader2
            v-if="isSubmitting"
            class="mr-2 h-4 w-4 animate-spin" />
          <FileUp
            v-else
            class="mr-2 h-4 w-4" />
          Lancer l'import
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
